import {DecimalPipe} from "@angular/common"
import {Component, computed, input, ChangeDetectionStrategy} from "@angular/core"
import {MatTooltipModule} from "@angular/material/tooltip"
import {FilesizePipe} from "@common/pipes/filesize/filesize.pipe"
import {FormatDatePipe} from "@common/pipes/format-date/format-date.pipe"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {Labels} from "@labels"
import {Size2Like} from "@cm/math"
import {ImageColorSpace} from "@generated"
import {ActionPanelRowComponent} from "@app/common/components/menu/actions/action-panel-row/action-panel-row.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-tiling-info-panel",
    templateUrl: "./tiling-info-panel.component.html",
    styleUrls: ["./tiling-info-panel.component.scss"],
    imports: [ActionPanelRowComponent, DecimalPipe, FilesizePipe, MatTooltipModule, TimeAgoPipe, FormatDatePipe],
})
export class TilingInfoPanelComponent {
    readonly $createdByName = input<string | undefined>(undefined, {alias: "createdByName"})
    readonly $createdAt = input<string | undefined>(undefined, {alias: "createdAt"})
    readonly $updatedByName = input<string | undefined>(undefined, {alias: "updatedByName"})
    readonly $updatedAt = input<string | undefined>(undefined, {alias: "updatedAt"})
    readonly $imageColorSpace = input<ImageColorSpace | undefined>(undefined, {alias: "imageColorSpace"})
    readonly $pxPerCm = input<number | Size2Like | undefined>(undefined, {alias: "pxPerCm"})
    readonly $resultResolution = input<Size2Like>({width: 0, height: 0}, {alias: "resultResolution"})
    readonly $resultFileSize = input<number | undefined>(undefined, {alias: "resultFileSize"})
    readonly $displacement = input<number | undefined>(undefined, {alias: "displacement"})
    readonly $individualPxPerCm = computed(() => {
        const pxPerCm = this.$pxPerCm()
        if (!pxPerCm) {
            return {width: 1, height: 1}
        }
        const widthPxPerCm = typeof pxPerCm === "number" ? pxPerCm : pxPerCm.width
        const heightPxPerCm = typeof pxPerCm === "number" ? pxPerCm : pxPerCm.height
        return {width: widthPxPerCm, height: heightPxPerCm}
    })
    readonly $uniformPxPerCm = computed(() => {
        const pxPerCm = this.$individualPxPerCm()
        return (pxPerCm.width + pxPerCm.height) / 2
    })
    readonly $individualDpi = computed(() => {
        const pxPerCm = this.$individualPxPerCm()
        return {width: pxPerCm.width * 2.54, height: pxPerCm.height * 2.54}
    })
    readonly $uniformDpi = computed(() => {
        return this.$uniformPxPerCm() * 2.54
    })
    readonly $hasSquarePixels = computed(() => {
        const pxPerCm = this.$individualPxPerCm()
        return Math.abs(pxPerCm.width - pxPerCm.height) < 1e-8
    })
    readonly $resultSize = computed(() => {
        const pxPerCm = this.$individualPxPerCm()
        const resultResolution = this.$resultResolution()
        return {
            width: resultResolution.width / pxPerCm.width,
            height: resultResolution.height / pxPerCm.height,
        }
    })
    readonly $imageColorSpaceLabel = computed(() => {
        const imageColorSpace = this.$imageColorSpace()
        return (imageColorSpace ? Labels.ImageColorSpace.get(imageColorSpace)?.label : undefined) ?? "Not set"
    })

    protected readonly Labels = Labels
}
